import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { sbFetch } from "../utils/supabaseClient";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import "../styles/ListTab.css";

const DashboardTab = forwardRef((props, ref) => {
  const [timeRange, setTimeRange] = useState(30);
  const [chartExpanded, setChartExpanded] = useState(false);
  const [breakdownView, setBreakdownView] = useState("weeks");
  const [expandedSections, setExpandedSections] = useState({
    newsClicks: false,
    resourceClicks: false,
    lessonsCompleted: false,
    promptsCopied: false,
    assessmentsTaken: false,
    oriUsage: false,
    registrations: false
  });
  const [metrics, setMetrics] = useState({
    newsClicks: { current: 0, previous: 0, change: 0 },
    resourceClicks: { current: 0, previous: 0, change: 0 },
    lessonsCompleted: { current: 0, previous: 0, change: 0 },
    promptsCopied: { current: 0, previous: 0, change: 0 },
    assessmentsTaken: { current: 0, previous: 0, change: 0 },
    oriUsage: { current: 0, previous: 0, change: 0 },
    registrations: { current: 0, previous: 0, change: 0 },
    loading: true
  });
  const [chartData, setChartData] = useState([]);
  const [wordCloudData, setWordCloudData] = useState([]);
  const [breakdownData, setBreakdownData] = useState({
    newsClicks: [],
    resourceClicks: [],
    lessonsCompleted: [],
    promptsCopied: [],
    assessmentsTaken: [],
    oriUsage: [],
    registrations: []
  });

  useImperativeHandle(ref, () => ({
    refreshData: loadDashboard
  }), [timeRange, breakdownView]);

  useEffect(() => {
    loadDashboard();
  }, []);

  const calcChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const TrendArrow = ({ change, size = 20 }) => {
    if (change > 0) {
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" style={{ display: "inline", flexShrink: 0 }}>
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 17"></polyline>
          <polyline points="17 6 23 6 23 12"></polyline>
        </svg>
      );
    } else if (change < 0) {
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" style={{ display: "inline", flexShrink: 0 }}>
          <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
          <polyline points="17 18 23 18 23 12"></polyline>
        </svg>
      );
    }
    return null;
  };

  const MetricCard = ({ title, current, previous, change, color }) => (
    <div style={{
      padding: "24px",
      background: `rgba(${color}, 0.08)`,
      border: `1px solid rgba(${color}, 0.2)`,
      borderRadius: "8px"
    }}>
      <h3 style={{ color: "#7A9CC4", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", margin: "0 0 12px 0", letterSpacing: "0.5px" }}>{title}</h3>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "12px", marginBottom: "8px" }}>
        <div style={{ color: `rgb(${color})`, fontSize: "32px", fontWeight: "700" }}>{current}</div>
        <div style={{
          color: change >= 0 ? "#10B981" : "#EF4444",
          fontSize: "14px",
          fontWeight: "600",
          background: change >= 0 ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
          padding: "6px 10px",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          gap: "8px"
        }}>
          {change >= 0 ? "+" : ""}{change}%
          <TrendArrow change={change} size={18} />
        </div>
      </div>
      <p style={{ color: "#9DB4D4", fontSize: "12px", margin: "0" }}>vs last {breakdownView === "weeks" ? "week" : "month"}: {previous}</p>
    </div>
  );

  const BreakdownList = ({ title, data, color }) => (
    <div style={{
      padding: "20px",
      background: `rgba(${color}, 0.05)`,
      border: `1px solid rgba(${color}, 0.2)`,
      borderRadius: "8px",
      marginBottom: "12px"
    }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {data.length > 0 ? (
          data.map((item, idx) => (
            <div key={idx} style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              background: "rgba(59, 130, 246, 0.05)",
              border: "1px solid rgba(59, 130, 246, 0.1)",
              borderRadius: "6px"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ color: "#fff", fontSize: "13px", fontWeight: "500" }}>{item.period}</span>
                <span style={{ color: "#9DB4D4", fontSize: "13px", fontWeight: "600" }}>{item.count}</span>
              </div>
              <div style={{
                color: item.change >= 0 ? "#10B981" : "#EF4444",
                fontSize: "12px",
                fontWeight: "600",
                background: item.change >= 0 ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
                padding: "6px 10px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                {item.change >= 0 ? "+" : ""}{item.change}%
                <TrendArrow change={item.change} size={16} />
              </div>
            </div>
          ))
        ) : (
          <div style={{ color: "#7A9CC4", fontSize: "13px", textAlign: "center", padding: "20px" }}>No data</div>
        )}
      </div>
    </div>
  );

  const CollapsibleSection = ({ title, isExpanded, onToggle, children, color }) => (
    <div style={{ marginBottom: "20px" }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          padding: "16px",
          background: `rgba(${color}, 0.08)`,
          border: `1px solid rgba(${color}, 0.2)`,
          borderRadius: "8px",
          color: `rgb(${color})`,
          fontSize: "14px",
          fontWeight: "600",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          transition: "all 0.2s"
        }}
        onMouseEnter={(e) => {
          e.target.style.background = `rgba(${color}, 0.12)`;
        }}
        onMouseLeave={(e) => {
          e.target.style.background = `rgba(${color}, 0.08)`;
        }}
      >
        <span>{title}</span>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      {isExpanded && (
        <div style={{ paddingTop: "12px" }}>
          {children}
        </div>
      )}
    </div>
  );

  const loadDashboard = async () => {
    try {
      setMetrics(prev => ({ ...prev, loading: true }));

      const now = new Date();
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const previousMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

      const [newsClicks, resourceClicks, lessons, prompts, assessments, registrations, oriUsage] = await Promise.all([
        sbFetch("GET", "news_clicks").catch(() => []),
        sbFetch("GET", "resource_clicks").catch(() => []),
        sbFetch("GET", "lessons_completed").catch(() => []),
        sbFetch("GET", "prompt_copies").catch(() => []),
        sbFetch("GET", "assessment_responses").catch(() => []),
        sbFetch("GET", "registrations").catch(() => []),
        sbFetch("GET", "ori_usage").catch(() => [])
      ]);

      const countInRange = (items, dateField, startDate, endDate) => {
        if (!Array.isArray(items)) return 0;
        return items.filter(item => {
          const itemDate = new Date(item[dateField]);
          return itemDate >= startDate && itemDate <= endDate;
        }).length;
      };

      const currentMonthEnd = now;
      const newsCurrentMonth = countInRange(newsClicks, 'clicked_at', currentMonthStart, currentMonthEnd);
      const resourceCurrentMonth = countInRange(resourceClicks, 'clicked_at', currentMonthStart, currentMonthEnd);
      const lessonCurrentMonth = countInRange(lessons, 'completed_at', currentMonthStart, currentMonthEnd);
      const promptCurrentMonth = countInRange(prompts, 'copied_at', currentMonthStart, currentMonthEnd);
      const assessmentCurrentMonth = countInRange(assessments, 'createdat', currentMonthStart, currentMonthEnd);
      const oriCurrentMonth = countInRange(oriUsage, 'sent_at', currentMonthStart, currentMonthEnd);

      const newsPreviousMonth = countInRange(newsClicks, 'clicked_at', previousMonthStart, previousMonthEnd);
      const resourcePreviousMonth = countInRange(resourceClicks, 'clicked_at', previousMonthStart, previousMonthEnd);
      const lessonPreviousMonth = countInRange(lessons, 'completed_at', previousMonthStart, previousMonthEnd);
      const promptPreviousMonth = countInRange(prompts, 'copied_at', previousMonthStart, previousMonthEnd);
      const assessmentPreviousMonth = countInRange(assessments, 'createdat', previousMonthStart, previousMonthEnd);
      const oriPreviousMonth = countInRange(oriUsage, 'sent_at', previousMonthStart, previousMonthEnd);

      const registCurrentMonth = countInRange(registrations, 'created_at', currentMonthStart, currentMonthEnd);
      const registPreviousMonth = countInRange(registrations, 'created_at', previousMonthStart, previousMonthEnd);

      setMetrics({
        newsClicks: { current: newsCurrentMonth, previous: newsPreviousMonth, change: calcChange(newsCurrentMonth, newsPreviousMonth) },
        resourceClicks: { current: resourceCurrentMonth, previous: resourcePreviousMonth, change: calcChange(resourceCurrentMonth, resourcePreviousMonth) },
        lessonsCompleted: { current: lessonCurrentMonth, previous: lessonPreviousMonth, change: calcChange(lessonCurrentMonth, lessonPreviousMonth) },
        promptsCopied: { current: promptCurrentMonth, previous: promptPreviousMonth, change: calcChange(promptCurrentMonth, promptPreviousMonth) },
        assessmentsTaken: { current: assessmentCurrentMonth, previous: assessmentPreviousMonth, change: calcChange(assessmentCurrentMonth, assessmentPreviousMonth) },
        oriUsage: { current: oriCurrentMonth, previous: oriPreviousMonth, change: calcChange(oriCurrentMonth, oriPreviousMonth) },
        registrations: { current: registCurrentMonth, previous: registPreviousMonth, change: calcChange(registCurrentMonth, registPreviousMonth) },
        loading: false
      });

      await loadBreakdownData("weeks", newsClicks, resourceClicks, lessons, prompts, assessments, registrations, oriUsage);
      await loadChartData(timeRange, newsClicks, resourceClicks, lessons, prompts, assessments, oriUsage);
      await loadWordCloudData("weeks");
    } catch (err) {
      console.error("Error loading dashboard:", err);
      setMetrics(prev => ({ ...prev, loading: false }));
    }
  };

  const loadBreakdownData = async (view, newsClicks, resourceClicks, lessons, prompts, assessments, registrations, oriUsage) => {
    try {
      const now = new Date();
      const periods = [];

      if (view === "weeks") {
        const today = new Date(now);
        const dayOfWeek = today.getDay();
        const lastSunday = new Date(today);
        lastSunday.setDate(today.getDate() - dayOfWeek);

        for (let i = 0; i < 4; i++) {
          const weekEnd = new Date(lastSunday);
          weekEnd.setDate(lastSunday.getDate() - (i * 7));
          const weekStart = new Date(weekEnd);
          weekStart.setDate(weekEnd.getDate() - 6);

          const prevWeekEnd = new Date(weekStart);
          prevWeekEnd.setDate(weekStart.getDate() - 1);
          const prevWeekStart = new Date(prevWeekEnd);
          prevWeekStart.setDate(prevWeekEnd.getDate() - 6);

          const countInRange = (items, dateField, startDate, endDate) => {
            if (!Array.isArray(items)) return 0;
            return items.filter(item => {
              const itemDate = new Date(item[dateField]);
              return itemDate >= startDate && itemDate <= endDate;
            }).length;
          };

          periods.push({
            period: `Week of ${weekStart.toLocaleDateString()}`,
            weekStart,
            weekEnd,
            prevWeekStart,
            prevWeekEnd,
            countInRange
          });
        }
      } else {
        for (let i = 5; i >= 0; i--) {
          const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
          const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);

          const prevMonthEnd = new Date(monthStart);
          prevMonthEnd.setDate(0);
          const prevMonthStart = new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1);

          const monthName = monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

          const countInRange = (items, dateField, startDate, endDate) => {
            if (!Array.isArray(items)) return 0;
            return items.filter(item => {
              const itemDate = new Date(item[dateField]);
              return itemDate >= startDate && itemDate <= endDate;
            }).length;
          };

          periods.push({
            period: monthName,
            weekStart: monthStart,
            weekEnd: monthEnd,
            prevWeekStart: prevMonthStart,
            prevWeekEnd: prevMonthEnd,
            countInRange
          });
        }
      }

      const countInRange = (items, dateField, startDate, endDate) => {
        if (!Array.isArray(items)) return 0;
        return items.filter(item => {
          const itemDate = new Date(item[dateField]);
          return itemDate >= startDate && itemDate <= endDate;
        }).length;
      };

      const newData = {
        newsClicks: periods.map(p => ({
          period: p.period,
          count: countInRange(newsClicks, 'clicked_at', p.weekStart, p.weekEnd),
          change: calcChange(
            countInRange(newsClicks, 'clicked_at', p.weekStart, p.weekEnd),
            countInRange(newsClicks, 'clicked_at', p.prevWeekStart, p.prevWeekEnd)
          )
        })),
        resourceClicks: periods.map(p => ({
          period: p.period,
          count: countInRange(resourceClicks, 'clicked_at', p.weekStart, p.weekEnd),
          change: calcChange(
            countInRange(resourceClicks, 'clicked_at', p.weekStart, p.weekEnd),
            countInRange(resourceClicks, 'clicked_at', p.prevWeekStart, p.prevWeekEnd)
          )
        })),
        lessonsCompleted: periods.map(p => ({
          period: p.period,
          count: countInRange(lessons, 'completed_at', p.weekStart, p.weekEnd),
          change: calcChange(
            countInRange(lessons, 'completed_at', p.weekStart, p.weekEnd),
            countInRange(lessons, 'completed_at', p.prevWeekStart, p.prevWeekEnd)
          )
        })),
        promptsCopied: periods.map(p => ({
          period: p.period,
          count: countInRange(prompts, 'copied_at', p.weekStart, p.weekEnd),
          change: calcChange(
            countInRange(prompts, 'copied_at', p.weekStart, p.weekEnd),
            countInRange(prompts, 'copied_at', p.prevWeekStart, p.prevWeekEnd)
          )
        })),
        assessmentsTaken: periods.map(p => ({
          period: p.period,
          count: countInRange(assessments, 'createdat', p.weekStart, p.weekEnd),
          change: calcChange(
            countInRange(assessments, 'createdat', p.weekStart, p.weekEnd),
            countInRange(assessments, 'createdat', p.prevWeekStart, p.prevWeekEnd)
          )
        })),
        oriUsage: periods.map(p => ({
          period: p.period,
          count: countInRange(oriUsage, 'sent_at', p.weekStart, p.weekEnd),
          change: calcChange(
            countInRange(oriUsage, 'sent_at', p.weekStart, p.weekEnd),
            countInRange(oriUsage, 'sent_at', p.prevWeekStart, p.prevWeekEnd)
          )
        })),
        registrations: periods.map(p => ({
          period: p.period,
          count: countInRange(registrations, 'created_at', p.weekStart, p.weekEnd),
          change: calcChange(
            countInRange(registrations, 'created_at', p.weekStart, p.weekEnd),
            countInRange(registrations, 'created_at', p.prevWeekStart, p.prevWeekEnd)
          )
        }))
      };

      setBreakdownData(newData);
    } catch (err) {
      console.error("Error loading breakdown data:", err);
    }
  };

  const loadChartData = async (days, newsClicks, resourceClicks, lessons, prompts, assessments, oriUsage) => {
    try {
      const now = new Date();
      const startDate = new Date(now);
      startDate.setDate(now.getDate() - days);

      const dateMap = {};

      for (let i = 0; i < days; i++) {
        const d = new Date(startDate);
        d.setDate(d.getDate() + i);
        const dateStr = d.toLocaleDateString();
        dateMap[dateStr] = { date: dateStr, news: 0, resources: 0, lessons: 0, prompts: 0, assessments: 0, ori: 0 };
      }

      const aggregateByDate = (items, dateField, key) => {
        if (!Array.isArray(items)) return;
        items.forEach(item => {
          const dateVal = item[dateField];
          if (!dateVal) return;
          const dateStr = new Date(dateVal).toLocaleDateString();
          if (dateMap[dateStr]) {
            dateMap[dateStr][key]++;
          }
        });
      };

      aggregateByDate(newsClicks, 'clicked_at', 'news');
      aggregateByDate(resourceClicks, 'clicked_at', 'resources');
      aggregateByDate(lessons, 'completed_at', 'lessons');
      aggregateByDate(prompts, 'copied_at', 'prompts');
      aggregateByDate(assessments, 'createdat', 'assessments');
      aggregateByDate(oriUsage, 'sent_at', 'ori');

      const chart = Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
      setChartData(chart);
    } catch (err) {
      console.error("Error loading chart data:", err);
    }
  };

  const loadWordCloudData = async (view = breakdownView) => {
    try {
      const now = new Date();
      const startDate = new Date(now);

      // Use last week for "weeks" view, last 30 days for "months" view
      const daysBack = view === "weeks" ? 7 : 30;
      startDate.setDate(now.getDate() - daysBack);

      // Fetch interaction data for the time period
      const [resourceClicks, lessonCompletes, promptCopies] = await Promise.all([
        sbFetch("GET", "resource_clicks").catch(() => []),
        sbFetch("GET", "lessons_completed").catch(() => []),
        sbFetch("GET", "prompt_copies").catch(() => [])
      ]);

      // Filter by date
      const countInRange = (items, dateField) => {
        if (!Array.isArray(items)) return [];
        return items.filter(item => {
          const itemDate = new Date(item[dateField]);
          return itemDate >= startDate && itemDate <= now;
        });
      };

      const resourcesInRange = countInRange(resourceClicks, 'clicked_at');
      const lessonsInRange = countInRange(lessonCompletes, 'completed_at');
      const promptsInRange = countInRange(promptCopies, 'copied_at');

      // Fetch content definitions to get categories
      const [resourcesContent, lessonsContent, promptsContent] = await Promise.all([
        sbFetch("GET", "app_settings?key=eq.origamic-resources").catch(() => []),
        sbFetch("GET", "app_settings?key=eq.origamic-lessons").catch(() => []),
        sbFetch("GET", "app_settings?key=eq.origamic-prompts").catch(() => [])
      ]);

      // Parse content
      let resources = [];
      if (Array.isArray(resourcesContent) && resourcesContent.length > 0) {
        try {
          resources = JSON.parse(resourcesContent[0].value) || [];
        } catch (e) {
          resources = [];
        }
      }

      let lessons = [];
      if (Array.isArray(lessonsContent) && lessonsContent.length > 0) {
        try {
          lessons = JSON.parse(lessonsContent[0].value) || [];
        } catch (e) {
          lessons = [];
        }
      }

      let prompts = [];
      if (Array.isArray(promptsContent) && promptsContent.length > 0) {
        try {
          prompts = JSON.parse(promptsContent[0].value) || [];
        } catch (e) {
          prompts = [];
        }
      }

      // Create lookup maps
      const resourceMap = {};
      resources.forEach(r => {
        resourceMap[r.id] = r.category;
      });

      const lessonMap = {};
      lessons.forEach(l => {
        lessonMap[l.id] = l.tag;
      });

      const promptMap = {};
      prompts.forEach(p => {
        promptMap[p.id] = p.cat;
      });

      // Count categories from interactions
      const categoryCounts = {};

      resourcesInRange.forEach(item => {
        const category = resourceMap[item.resource_id];
        if (category) {
          categoryCounts[category] = (categoryCounts[category] || 0) + 1;
        }
      });

      lessonsInRange.forEach(item => {
        const category = lessonMap[item.lesson_id];
        if (category) {
          categoryCounts[category] = (categoryCounts[category] || 0) + 1;
        }
      });

      promptsInRange.forEach(item => {
        const category = promptMap[item.prompt_id];
        if (category) {
          categoryCounts[category] = (categoryCounts[category] || 0) + 1;
        }
      });

      // Generate word cloud data with sizes
      const maxCount = Math.max(...Object.values(categoryCounts), 1);
      const wordCloudArray = Object.entries(categoryCounts)
        .map(([category, count]) => ({
          category,
          count,
          size: 12 + (count / maxCount) * 32 // Font size between 12 and 44
        }))
        .sort(() => Math.random() - 0.5); // Shuffle for visual variety

      setWordCloudData(wordCloudArray);
    } catch (err) {
      console.error("Error loading word cloud data:", err);
    }
  };

  useEffect(() => {
    if (metrics.loading === false) {
      const reloadData = async () => {
        const [newsClicks, resourceClicks, lessons, prompts, assessments, registrations, oriUsage] = await Promise.all([
          sbFetch("GET", "news_clicks").catch(() => []),
          sbFetch("GET", "resource_clicks").catch(() => []),
          sbFetch("GET", "lessons_completed").catch(() => []),
          sbFetch("GET", "prompt_copies").catch(() => []),
          sbFetch("GET", "assessment_responses").catch(() => []),
          sbFetch("GET", "registrations").catch(() => []),
          sbFetch("GET", "ori_usage").catch(() => [])
        ]);
        await loadBreakdownData(breakdownView, newsClicks, resourceClicks, lessons, prompts, assessments, registrations, oriUsage);
        await loadChartData(timeRange, newsClicks, resourceClicks, lessons, prompts, assessments, oriUsage);
        await loadWordCloudData(breakdownView);
      };
      reloadData();
    }
  }, [timeRange, breakdownView]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="list-container" style={{ height: "calc(100vh - 120px)", overflowY: "auto", paddingRight: "20px" }}>
        <div style={{ marginBottom: "40px" }}>
          {!metrics.loading && (
            <>
              {/* Breakdown View Filter - Moved to Top */}
              <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
                <button
                  onClick={() => setBreakdownView("weeks")}
                  style={{
                    padding: "8px 16px",
                    background: breakdownView === "weeks" ? "rgba(59, 130, 246, 0.2)" : "transparent",
                    border: breakdownView === "weeks" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                    color: breakdownView === "weeks" ? "#5B9EF0" : "#7A9CC4",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "600",
                    transition: "all 0.2s"
                  }}
                >
                  By Week
                </button>
                <button
                  onClick={() => setBreakdownView("months")}
                  style={{
                    padding: "8px 16px",
                    background: breakdownView === "months" ? "rgba(59, 130, 246, 0.2)" : "transparent",
                    border: breakdownView === "months" ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                    color: breakdownView === "months" ? "#5B9EF0" : "#7A9CC4",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "600",
                    transition: "all 0.2s"
                  }}
                >
                  By Month
                </button>
              </div>

              {/* Registrations - Moved to Top */}
              <div style={{ background: "rgba(16, 185, 129, 0.05)", borderRadius: "8px", border: "1px solid rgba(16, 185, 129, 0.2)", padding: "24px", marginBottom: "40px" }}>
                <h3 style={{ color: "#10B981", fontSize: "16px", fontWeight: "600", margin: "0 0 20px 0" }}>Registration Growth</h3>
                <MetricCard title="Growth from last month" current={metrics.registrations.current} previous={metrics.registrations.previous} change={metrics.registrations.change} color="16, 185, 129" />
              </div>

              <CollapsibleSection
                title="Registrations Details"
                isExpanded={expandedSections.registrations}
                onToggle={() => toggleSection("registrations")}
                color="16, 185, 129"
              >
                <BreakdownList title="Registrations" data={breakdownData.registrations} color="16, 185, 129" />
              </CollapsibleSection>

              {/* Content Interaction Metrics */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "20px", marginBottom: "40px" }}>
                <MetricCard title="News Clicks" current={metrics.newsClicks.current} previous={metrics.newsClicks.previous} change={metrics.newsClicks.change} color="245, 158, 11" />
                <MetricCard title="Resource Clicks" current={metrics.resourceClicks.current} previous={metrics.resourceClicks.previous} change={metrics.resourceClicks.change} color="251, 191, 36" />
                <MetricCard title="Lessons Completed" current={metrics.lessonsCompleted.current} previous={metrics.lessonsCompleted.previous} change={metrics.lessonsCompleted.change} color="52, 211, 153" />
                <MetricCard title="Prompts Copied" current={metrics.promptsCopied.current} previous={metrics.promptsCopied.previous} change={metrics.promptsCopied.change} color="168, 85, 247" />
                <MetricCard title="Assessments Taken" current={metrics.assessmentsTaken.current} previous={metrics.assessmentsTaken.previous} change={metrics.assessmentsTaken.change} color="59, 130, 246" />
                <MetricCard title="ORI Usage" current={metrics.oriUsage.current} previous={metrics.oriUsage.previous} change={metrics.oriUsage.change} color="236, 72, 153" />

                {/* Word Cloud - Categories (double width) */}
                <div style={{ gridColumn: "span 2", padding: "20px", background: "rgba(168, 85, 247, 0.08)", border: "1px solid rgba(168, 85, 247, 0.2)", borderRadius: "8px", minHeight: "160px" }}>
                  <h3 style={{ color: "#A855F7", margin: "0 0 12px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Categories Word Cloud</h3>
                  {wordCloudData && wordCloudData.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center", padding: "8px 0" }}>
                      {wordCloudData.map((item, idx) => (
                        <span
                          key={idx}
                          style={{
                            fontSize: `${item.size}px`,
                            color: `hsl(${Math.random() * 60 + 200}, 70%, 50%)`,
                            fontWeight: Math.random() > 0.5 ? "600" : "400",
                            opacity: 0.7 + item.count / (Math.max(...wordCloudData.map(w => w.count), 1)) * 0.3,
                            cursor: "default"
                          }}
                        >
                          {item.category}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: "#7A9CC4", fontSize: "13px", padding: "20px 0", textAlign: "center" }}>No categories in selected period</div>
                  )}
                </div>
              </div>

              {/* Content Click Breakdowns - Single Collapsible Section */}
              <div style={{ marginBottom: "20px" }}>
                <button
                  onClick={() => toggleSection("newsClicks")}
                  style={{
                    width: "100%",
                    padding: "16px",
                    background: "rgba(59, 130, 246, 0.08)",
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                    borderRadius: "8px",
                    color: "#5B9EF0",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    transition: "all 0.2s"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "rgba(59, 130, 246, 0.08)";
                  }}
                >
                  <span>Content Click Details</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: expandedSections.newsClicks ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                {expandedSections.newsClicks && (
                  <div style={{ paddingTop: "16px", display: "flex", flexDirection: "column", gap: "20px" }}>
                    <div>
                      <h4 style={{ color: "#F59E0B", margin: "0 0 12px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>News Clicks</h4>
                      <BreakdownList title="News Clicks" data={breakdownData.newsClicks} color="245, 158, 11" />
                    </div>
                    <div>
                      <h4 style={{ color: "#FBBF24", margin: "0 0 12px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Resource Clicks</h4>
                      <BreakdownList title="Resource Clicks" data={breakdownData.resourceClicks} color="251, 191, 36" />
                    </div>
                    <div>
                      <h4 style={{ color: "#34D399", margin: "0 0 12px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Lessons Completed</h4>
                      <BreakdownList title="Lessons Completed" data={breakdownData.lessonsCompleted} color="52, 211, 153" />
                    </div>
                    <div>
                      <h4 style={{ color: "#A855F7", margin: "0 0 12px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Prompts Copied</h4>
                      <BreakdownList title="Prompts Copied" data={breakdownData.promptsCopied} color="168, 85, 247" />
                    </div>
                    <div>
                      <h4 style={{ color: "#3B82F6", margin: "0 0 12px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>Assessments Taken</h4>
                      <BreakdownList title="Assessments Taken" data={breakdownData.assessmentsTaken} color="59, 130, 246" />
                    </div>
                    <div>
                      <h4 style={{ color: "#EC4899", margin: "0 0 12px 0", fontSize: "13px", fontWeight: "600", textTransform: "uppercase" }}>ORI Usage</h4>
                      <BreakdownList title="ORI Usage" data={breakdownData.oriUsage} color="236, 72, 153" />
                    </div>
                  </div>
                )}
              </div>

              {/* Trend Chart */}
              <div style={{ padding: "20px", background: "rgba(59, 130, 246, 0.05)", borderRadius: "8px", border: "1px solid rgba(59, 130, 246, 0.2)", marginTop: "40px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                  <div>
                    <h3 style={{ color: "#5B9EF0", margin: "0 0 12px 0", fontSize: "16px", fontWeight: "600" }}>Content Interactions Trend</h3>
                    <div style={{ display: "flex", gap: "12px" }}>
                      {[7, 30, 60, 90].map(days => (
                        <button
                          key={days}
                          onClick={() => setTimeRange(days)}
                          style={{
                            padding: "8px 16px",
                            background: timeRange === days ? "rgba(59, 130, 246, 0.2)" : "transparent",
                            border: timeRange === days ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                            color: timeRange === days ? "#5B9EF0" : "#7A9CC4",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "600",
                            transition: "all 0.2s"
                          }}
                        >
                          {days} Days
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => loadDashboard()}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(59, 130, 246, 0.3)",
                        borderRadius: "6px",
                        color: "#5B9EF0",
                        cursor: "pointer",
                        padding: "8px 12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "rgba(59, 130, 246, 0.1)";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
                      }}
                      title="Refresh data"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                      </svg>
                      Refresh
                    </button>
                    <button
                      onClick={() => setChartExpanded(!chartExpanded)}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(59, 130, 246, 0.3)",
                        borderRadius: "6px",
                        color: "#5B9EF0",
                        cursor: "pointer",
                        padding: "8px 12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "rgba(59, 130, 246, 0.1)";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.5)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "transparent";
                      e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
                    }}
                  >
                    {chartExpanded ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                        </svg>
                        Collapse
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M8 3v6a2 2 0 0 0 2 2h6V3M3 8h6a2 2 0 0 1 2 2v6h6V8a2 2 0 0 0-2-2h-6V3m0 18v-6a2 2 0 0 1 2-2h6v6h6v6a2 2 0 0 1-2 2h-6v-6h-6z" />
                        </svg>
                        Expand
                      </>
                    )}
                  </button>
                </div>
              </div>

                {chartData && chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(59, 130, 246, 0.1)" />
                      <XAxis dataKey="date" stroke="#7A9CC4" style={{ fontSize: "12px" }} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#7A9CC4" style={{ fontSize: "12px" }} />
                      <Tooltip contentStyle={{ background: "#0a1628", border: "1px solid rgba(59, 130, 246, 0.3)", borderRadius: "6px", color: "#fff" }} cursor={{ fill: "rgba(59, 130, 246, 0.05)" }} />
                      <Legend />
                      <Bar dataKey="news" stackId="a" fill="#F59E0B" name="News Clicks" />
                      <Bar dataKey="resources" stackId="a" fill="#FBBF24" name="Resource Clicks" />
                      <Bar dataKey="lessons" stackId="a" fill="#34D399" name="Lessons Completed" />
                      <Bar dataKey="prompts" stackId="a" fill="#A855F7" name="Prompts Copied" />
                      <Bar dataKey="assessments" stackId="a" fill="#3B82F6" name="Assessments Taken" />
                      <Bar dataKey="ori" stackId="a" fill="#EC4899" name="ORI Usage" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ color: "#7A9CC4", textAlign: "center", padding: "40px", fontSize: "14px" }}>Loading chart data...</div>
                )}
              </div>

              {/* Expanded Chart Modal */}
              {chartExpanded && (
                <div style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0, 0, 0, 0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10000,
                  padding: "20px"
                }}>
                  <div style={{
                    background: "#0a1628",
                    borderRadius: "12px",
                    border: "1px solid rgba(59, 130, 246, 0.3)",
                    width: "100%",
                    maxWidth: "90vw",
                    maxHeight: "90vh",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "auto"
                  }}>
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "20px",
                      borderBottom: "1px solid rgba(59, 130, 246, 0.2)"
                    }}>
                      <h2 style={{ color: "#5B9EF0", margin: "0", fontSize: "18px", fontWeight: "600" }}>Content Interactions Trend</h2>
                      <button
                        onClick={() => setChartExpanded(false)}
                        style={{
                          background: "transparent",
                          border: "1px solid rgba(59, 130, 246, 0.3)",
                          borderRadius: "6px",
                          color: "#5B9EF0",
                          cursor: "pointer",
                          padding: "8px 12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          transition: "all 0.2s"
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "rgba(59, 130, 246, 0.1)";
                          e.target.style.borderColor = "rgba(59, 130, 246, 0.5)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "transparent";
                          e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                    <div style={{ flex: 1, padding: "20px", overflow: "auto" }}>
                      {chartData && chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={500}>
                          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(59, 130, 246, 0.1)" />
                            <XAxis dataKey="date" stroke="#7A9CC4" style={{ fontSize: "12px" }} angle={-45} textAnchor="end" height={80} />
                            <YAxis stroke="#7A9CC4" style={{ fontSize: "12px" }} />
                            <Tooltip contentStyle={{ background: "#0a1628", border: "1px solid rgba(59, 130, 246, 0.3)", borderRadius: "6px", color: "#fff" }} cursor={{ fill: "rgba(59, 130, 246, 0.05)" }} />
                            <Legend />
                            <Bar dataKey="news" stackId="a" fill="#F59E0B" name="News Clicks" />
                            <Bar dataKey="resources" stackId="a" fill="#FBBF24" name="Resource Clicks" />
                            <Bar dataKey="lessons" stackId="a" fill="#34D399" name="Lessons Completed" />
                            <Bar dataKey="prompts" stackId="a" fill="#A855F7" name="Prompts Copied" />
                            <Bar dataKey="assessments" stackId="a" fill="#3B82F6" name="Assessments Taken" />
                            <Bar dataKey="ori" stackId="a" fill="#EC4899" name="ORI Usage" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div style={{ color: "#7A9CC4", textAlign: "center", padding: "40px" }}>No chart data</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {metrics.loading && (
            <div style={{ color: "#7A9CC4", textAlign: "center", padding: "40px" }}>Loading dashboard...</div>
          )}
        </div>
    </div>
  );
});

export default DashboardTab;
